/**
 * Ticket KN1: Logik des Keynote-Bereichs.
 *
 * Bewusst reine Logik – keine Datenbank, kein React. Damit bleiben die
 * Zusicherungen des Tickets dauerhaft prüfbar:
 *  – Ein zurückgezogenes Thema (Status „entwurf") erscheint nicht auf der Seite.
 *  – Ein Referent erscheint nur mit Status „veröffentlicht" UND mit einem
 *    Teamprofil, das das Kennzeichen „auf der Website zeigen" trägt.
 *  – Eine Anfrage ohne Termin, Ort, Name oder E-Mail wird abgewiesen.
 *
 * Personen kommen ausschließlich aus den Teamprofilen (siehe `team-people.ts`).
 * Es gibt keine zweite Personenverwaltung: Foto, Name, Funktion und
 * Vorstellungstext werden am Profil gepflegt.
 */

import { z } from "zod";
import type { PublicPerson } from "@/lib/team-people";

export const KEYNOTE_PAGE_PATH = "/wissen/keynotes";

/** Sprungziel des Anfrageformulars am Seitenende. */
export const KEYNOTE_ANCHORS = {
  anfrage: "anfrage",
} as const;

export const KEYNOTE_STATUSES = ["entwurf", "veroeffentlicht"] as const;
export type KeynoteStatus = (typeof KEYNOTE_STATUSES)[number];

export const KEYNOTE_STATUS_LABELS: Record<KeynoteStatus, string> = {
  entwurf: "Zurückgezogen",
  veroeffentlicht: "Sichtbar",
};

export type KeynoteTopic = {
  id: string;
  heading: string;
  bodyMd: string;
  sortOrder: number;
  status: KeynoteStatus;
};

export type KeynoteSpeakerRow = {
  id: string;
  profileId: string;
  sortOrder: number;
  status: KeynoteStatus;
};

/** Ein Referent, wie ihn die öffentliche Seite braucht. */
export type KeynoteSpeaker = {
  id: string;
  /** Sprungziel der Namensleiste am Seitenanfang. */
  anchorId: string;
  person: PublicPerson;
  topics: KeynoteTopic[];
};

export function parseKeynoteStatus(value: unknown): KeynoteStatus {
  return value === "veroeffentlicht" ? "veroeffentlicht" : "entwurf";
}

/**
 * Sprungziel aus dem Namen. Umlaute werden ausgeschrieben, alles Übrige fällt
 * weg; ohne verwertbaren Namen dient die Kennung als Anker.
 */
export function keynoteAnchorId(name: string, fallback: string): string {
  const slug = name
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return slug ? `referent-${slug}` : `referent-${fallback}`;
}

/**
 * Öffentliche Referentenliste bauen.
 *
 * Zwei Bedingungen je Referent (beide nötig, keine ist überflüssig): eigener
 * Status „veröffentlicht" und ein freigegebenes Teamprofil. Themen werden
 * zusätzlich einzeln nach Status gefiltert – ein zurückgezogenes Thema bleibt
 * erhalten, erscheint aber nicht.
 */
export function buildKeynoteSpeakers(
  speakers: KeynoteSpeakerRow[],
  topics: Array<KeynoteTopic & { speakerId: string }>,
  people: Record<string, PublicPerson>,
): KeynoteSpeaker[] {
  return speakers
    .filter((speaker) => speaker.status === "veroeffentlicht")
    .filter((speaker) => Boolean(people[speaker.profileId]))
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((speaker) => {
      const person = people[speaker.profileId];
      return {
        id: speaker.id,
        anchorId: keynoteAnchorId(person.name, speaker.id),
        person,
        topics: topics
          .filter((topic) => topic.speakerId === speaker.id)
          .filter((topic) => topic.status === "veroeffentlicht")
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map(({ speakerId: _speakerId, ...topic }) => topic),
      };
    });
}

/**
 * Eingaben des Anfrageformulars.
 *
 * Pflicht sind Termin, Ort, Name und E-Mail-Adresse. Alles Übrige ist
 * freiwillig: Wer den Anlass nicht beschreiben mag, soll trotzdem anfragen
 * können. Die Wunschperson ist eine Auswahl aus den Referenten; „ohne Angabe"
 * ist der leere Wert.
 */
export const keynoteInquirySchema = z.object({
  dateText: z.string().trim().min(1, "Bitte Termin oder Zeitraum angeben").max(200),
  location: z.string().trim().min(1, "Bitte Ort angeben").max(200),
  name: z.string().trim().min(1, "Bitte Namen angeben").max(200),
  email: z.string().trim().email("Ungültige E-Mail-Adresse").max(255),
  organisation: z.string().trim().max(200).optional().or(z.literal("")),
  phone: z.string().trim().max(40).optional().or(z.literal("")),
  occasion: z.string().trim().max(500).optional().or(z.literal("")),
  audienceText: z.string().trim().max(500).optional().or(z.literal("")),
  groupSize: z.string().trim().max(80).optional().or(z.literal("")),
  /** Kennung eines Referenten; leer bedeutet „ohne Angabe". */
  speakerId: z.string().uuid().optional().or(z.literal("")),
  topicWish: z.string().trim().max(2000).optional().or(z.literal("")),
  turnstileToken: z.string().min(1, "Bot-Schutz erforderlich").max(2048),
});

export type KeynoteInquiryInput = z.infer<typeof keynoteInquirySchema>;

/** Auswahlwert „ohne Angabe" der Wunschperson. */
export const KEYNOTE_SPEAKER_ANY = "";
export const KEYNOTE_SPEAKER_ANY_LABEL = "Ohne Angabe";

/**
 * Name aus einem Feld in Vor- und Nachnamen zerlegen. Ein einzelnes Wort gilt
 * als Nachname – geraten wird nichts.
 */
export function splitKeynoteName(name: string): { firstName: string | null; lastName: string } {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 1) return { firstName: null, lastName: parts[0] ?? "" };
  return { firstName: parts.slice(0, -1).join(" "), lastName: parts.at(-1) as string };
}

/**
 * Alle Angaben einer Anfrage als lesbare Zeilen – für die Benachrichtigungsmail
 * und die Ansicht im Anfrageeingang. Leere freiwillige Felder entfallen.
 */
export function keynoteInquirySummary(
  input: KeynoteInquiryInput,
  speakerName: string | null,
): string {
  const lines: Array<[string, string]> = [
    ["Termin oder Zeitraum", input.dateText],
    ["Ort", input.location],
    ["Anlass", input.occasion ?? ""],
    ["Publikum", input.audienceText ?? ""],
    ["Gruppengröße", input.groupSize ?? ""],
    ["Wunschperson", speakerName ?? (input.speakerId ? "" : KEYNOTE_SPEAKER_ANY_LABEL)],
    ["Wunschthema", input.topicWish ?? ""],
    ["Organisation", input.organisation ?? ""],
  ];
  return lines
    .filter(([, value]) => value.trim().length > 0)
    .map(([label, value]) => `${label}: ${value.trim()}`)
    .join("\n");
}
